// Дните зад един отрязък — „кой ден вдигна 13:00 – 13:59".
//
// В режим „слято" един ред събира много дни и въпросът се чете само тук.
// Данните идват от `days` на същия отговор — модалът не прави заявка.

import { CloseOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../../../../utils/modalStyles";
import { fmtEuro } from "../../../../utils/currency";
import type { PeriodRow } from "./salesByPeriodsMath";

type Props = {
  /** Кликнатият ред; `null` държи модала затворен. */
  row: PeriodRow | null;
  onClose: () => void;
};

export const PeriodDaysModal = ({ row, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const width = isMobile ? undefined : isTablet ? 480 : 560;

  const days = row?.days ?? [];
  const max = Math.max(1, ...days.map((day) => day.revenue));

  return (
    <Modal
      open={row !== null}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="flex max-h-[82vh] flex-col bg-canvas">
        <header
          className={`flex shrink-0 items-start gap-3 border-b border-shade-2 bg-surface ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-5"
          }`}
        >
          <span className="grid size-10 shrink-0 place-items-center rounded-[10px] bg-brand-tint">
            <ClockCircleOutlined className="text-[20px] text-brand-deep-ink" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-[17px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
              {row?.label}
              {row?.sublabel ? (
                <span className="font-medium text-muted">{` · ${row.sublabel}`}</span>
              ) : null}
            </h2>
            <p className="mt-1 text-[13px] font-medium leading-[1.4] text-soft">
              {row
                ? t("reportsPage.salesPeriodsDetailSubtitle", {
                    bills: row.bills,
                    amount: fmtEuro(row.revenue),
                  })
                : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("reportsPage.stationsDetailClose")}
            className="grid size-[30px] shrink-0 place-items-center rounded-lg text-muted transition-colors hover:bg-shade-1 hover:text-ink"
          >
            <CloseOutlined className="text-[14px]" />
          </button>
        </header>

        <div
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
            isNarrow ? "px-3 py-3" : "px-[18px] py-4"
          }`}
        >
          {days.length === 0 ? (
            <p className="py-10 text-center text-[13px] font-medium text-soft">
              {t("reportsPage.salesPeriodsEmptyTitle")}
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {days.map((day) => {
                const date = dayjs(day.date);
                return (
                  <li
                    key={day.date}
                    className="flex flex-col gap-1.5 rounded-xl border border-shade-2 bg-surface px-4 py-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
                          {date.toDate().toLocaleDateString(i18n.language, {
                            weekday: "long",
                          })}
                        </span>
                        <span className="mt-1 font-pos-mono text-[14px] font-bold leading-none tabular-nums text-ink">
                          {date.format("DD.MM.YYYY")}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[11.5px] font-medium text-muted">
                          {`${day.bills} ${t("reportsPage.bills").toLowerCase()}`}
                        </span>
                        <span className="font-pos-mono text-[15px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-ink">
                          {fmtEuro(day.revenue)}
                        </span>
                      </div>
                    </div>
                    <div className="flex h-1.5 overflow-hidden rounded-full bg-fill">
                      {/* Делът се знае чак по време на изпълнение. */}
                      <span
                        className="rounded-full bg-brand"
                        style={{ width: `${(day.revenue / max) * 100}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer
          className={`flex shrink-0 items-center justify-between gap-3 border-t border-shade-2 bg-surface ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-4"
          }`}
        >
          <span className="font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
            {t("reportsPage.total")}
          </span>
          <div className="flex items-center gap-3">
            <span className="font-pos-mono text-[18px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-ink">
              {fmtEuro(row?.revenue ?? 0)}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="h-10 rounded-[10px] border border-shade-3 bg-surface px-4 text-[13px] font-bold text-soft transition-colors hover:bg-fill"
            >
              {t("reportsPage.stationsDetailClose")}
            </button>
          </div>
        </footer>
      </div>
    </Modal>
  );
};
